/**
 * Probes the new payment backend (cloud-payment-service) to decide whether a
 * merchant has been migrated to the new payment service.
 *
 * Decision rule:
 *   - HTTP 2xx with a non-empty `data` array => migrated (resolves true)
 *   - empty data / 404 / 401 / 403 / 5xx / network error => not migrated (resolves false)
 *
 * The new backend only returns payment-method entries for merchants configured on it,
 * so any entry at all means the merchant has been migrated.
 *
 * Fail-safe by design: this never rejects — any uncertainty resolves to `false`
 * so the user simply stays on this (old) UI.
 *
 * Note: we intentionally do NOT reuse `HTTPRequest`, because its error path posts
 * every non-2xx body to `window.ReactNativeWebView`. Benign 404/401 responses from
 * the probe must not reach the mobile app's error channel.
 *
 * The access token is forwarded verbatim (no `Bearer ` prefix) to match exactly how
 * the new UI (payment-web-ui) authenticates against the same backend.
 */
function probeMerchantMigration({ newApiOrigin, merchant, accessToken }) {
    if (!newApiOrigin || !merchant || !accessToken) {
        return Promise.resolve(false);
    }

    const url = `${newApiOrigin}/api/v2/payment/merchant/${encodeURIComponent(merchant)}/online/paymentmethods`;

    return fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: accessToken,
        },
    })
        .then(response => {
            if (response.status < 200 || response.status >= 300) {
                return false;
            }

            return response.json()
                .then(body => Array.isArray(body?.data) && body.data.length > 0)
                .catch(() => false);
        })
        .catch(() => false);
}

export default probeMerchantMigration;
